import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { RotateCcw, PenLine } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { StatusChip, SecureImage, usePoll, PageHeader } from "@/components/common";

const STEPS = ["saved", "extracting", "complete"];

export default function CaptureProgress() {
  const { id } = useParams();
  const nav = useNavigate();
  const [cap, setCap] = useState(null);
  usePoll(async () => {
    try {
      const { data } = await api.get(`/captures/${id}`);
      setCap(data);
      if (["complete", "needs_review"].includes(data.status) && data.contact_ids?.length) nav(`/contacts/${data.contact_ids[0]}?review=1`, { replace: true });
    } catch (e) { toast.error(errMsg(e)); }
  }, 2000, [id]);

  const retry = async () => { try { await api.post(`/captures/${id}/retry`); toast.success("Retrying extraction"); } catch (e) { toast.error(errMsg(e)); } };
  const manual = async () => { try { const { data } = await api.post(`/captures/${id}/manual`); nav(`/contacts/${data.contact_id}?review=1`); } catch (e) { toast.error(errMsg(e)); } };

  if (!cap) return <p className="text-slate-500">Loading…</p>;
  const idx = cap.status === "failed" ? -1 : STEPS.indexOf(cap.status === "needs_review" ? "complete" : cap.status);
  return (
    <div className="max-w-2xl">
      <PageHeader title="Processing capture" subtitle="Your source is saved. You can leave this page — extraction continues in the background." />
      <div className="cd-card space-y-5" data-testid="capture-progress">
        <div className="flex items-center gap-2">
          {STEPS.map((s, i) => (
            <div key={s} className="flex flex-1 flex-col gap-1.5">
              <div className={`h-1.5 rounded-full ${i <= idx ? "bg-teal-600" : "bg-slate-200"}`} />
              <span className="text-xs font-medium text-slate-500">{s === "saved" ? "Saved" : s === "extracting" ? "Extracting" : "Ready for review"}</span>
            </div>
          ))}
        </div>
        <div className="flex items-center justify-between"><span className="text-sm text-slate-600">Status</span><StatusChip status={cap.status} testid="capture-status" /></div>
        {cap.file_ids?.length > 0 && (
          <div className="grid grid-cols-2 gap-3">{cap.file_ids.map((f) => <SecureImage key={f} fileId={f} className="h-44 w-full rounded-xl bg-slate-50 object-contain" />)}</div>
        )}
        {cap.raw_text && <pre className="max-h-40 overflow-auto whitespace-pre-wrap rounded-xl bg-slate-50 p-3 text-xs">{cap.raw_text}</pre>}
        {cap.status === "failed" && (
          <div className="space-y-3">
            <p data-testid="capture-error" className="rounded-xl bg-rose-50 p-3 text-sm text-rose-700">{cap.error} — the original is kept.</p>
            <div className="flex flex-wrap gap-2">
              {!cap.contact_ids?.length && <button data-testid="capture-retry-btn" onClick={retry} className="cd-btn-primary"><RotateCcw className="h-4 w-4" />Retry extraction</button>}
              <button data-testid="capture-manual-btn" onClick={manual} className="cd-btn-secondary"><PenLine className="h-4 w-4" />{cap.contact_ids?.length ? "Complete manually" : "Enter manually"}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
